/** @module Page header: the design's top bar on wide screens, the app bar on phones. */
import { Link, useNavigate } from "@tanstack/react-router";
import { ScanLine, Search } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { FeedbackButton } from "@/components/feedback-sheet";

/** True when typing would land in a field; "/" then belongs to the field. */
function typingIn(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/** The desktop search field: "/" focuses it, Enter opens the search page. */
function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const field = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.ctrlKey || event.metaKey || typingIn(event.target)) return;
      if (document.querySelector("dialog[open]")) return;
      event.preventDefault();
      field.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <form
      role="search"
      className="input-search w-[min(420px,32vw)]"
      onSubmit={(event) => {
        event.preventDefault();
        const query = text.trim();
        if (query) void navigate({ to: "/search", search: { q: query } });
      }}
    >
      <Search />
      <input
        ref={field}
        type="search"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
        }}
        placeholder={t("shell.searchPlaceholder")}
        aria-label={t("nav.search")}
        className="input pr-10"
      />
      <span className="kbd absolute right-3" aria-hidden="true">
        /
      </span>
    </form>
  );
}

/**
 * Title, optional subtitle and actions. On a wide screen the design's top bar also
 * carries the search field and the scan button, on every page.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  back,
  global = true,
  numeric = false,
}: {
  /** Left out where the page's own content carries the heading (card page). */
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** A link shown before the title, e.g. back to the list. */
  back?: ReactNode;
  /** Search and scan in the top bar; off for screens that are themselves search or scan. */
  global?: boolean;
  /** Figures in the subtitle ("1.284 Karten · 4.812,37 €") take the number font. */
  numeric?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <header className="-mx-4 -mt-4 mb-2 flex min-h-[52px] items-center justify-between gap-3 px-4 py-2 md:-mx-8 md:-mt-8 md:mb-0 md:gap-4 md:border-b md:border-(--border) md:px-8 md:py-4">
      <div className="row min-w-0 gap-3">
        {back}
        {title ? (
          <div className="flex min-w-0 flex-col">
            <h1 className="t-h2 truncate md:text-[length:var(--text-3xl)]">{title}</h1>
            {subtitle ? (
              <span className={`t-sm t-muted truncate ${numeric ? "num" : ""}`}>{subtitle}</span>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="row shrink-0 gap-3">
        {actions}
        <FeedbackButton />
        {global ? (
          <>
            <Link to="/search" className="icon-btn md:hidden" aria-label={t("nav.search")}>
              <Search />
            </Link>
            <span className="hidden md:block">
              <GlobalSearch />
            </span>
            <Link to="/scan" className="btn btn-primary hidden md:inline-flex">
              <ScanLine />
              {t("nav.scan")}
            </Link>
          </>
        ) : null}
      </div>
    </header>
  );
}
